import { Avatar, Card, CardContent, Stack, Typography, Box } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface AnalyticsKpiCardProps {
  title: string;
  value: string | number;
  subtext?: string;
  changePercent?: number | null;
  previousLabel?: string;
  icon?: React.ReactNode;
  invertTrend?: boolean;
  sx?: object;
}

export const AnalyticsKpiCard = ({
  title,
  value,
  subtext,
  changePercent,
  previousLabel,
  icon,
  invertTrend = false,
  sx,
}: AnalyticsKpiCardProps) => {
  const hasChange = changePercent !== undefined && changePercent !== null;
  const change = hasChange ? (changePercent as number) : 0;
  const valueWentUp = hasChange && change > 0;
  const valueWentDown = hasChange && change < 0;
  // invertTrend: higher is bad (e.g. prep time) — flip which direction is "good" color
  const isGood = hasChange
    ? invertTrend
      ? change < 0
      : change > 0
    : false;
  const isBad = hasChange
    ? invertTrend
      ? change > 0
      : change < 0
    : false;

  return (
    <Card sx={{ height: '100%', ...sx }}>
      <CardContent sx={{ pt: 2, pb: 2, px: 2.5 }}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography
              color='text.secondary'
              variant='overline'
              sx={{ fontSize: '0.7rem', letterSpacing: 0.5 }}
            >
              {title}
            </Typography>
            <Typography variant='h4'>{value}</Typography>
            {hasChange && (
              <Stack direction='row' alignItems='center' spacing={0.5}>
                {valueWentUp && (
                  <ArrowUpwardIcon
                    sx={{
                      fontSize: 14,
                      color: isGood ? 'success.main' : 'error.main',
                    }}
                  />
                )}
                {valueWentDown && (
                  <ArrowDownwardIcon
                    sx={{
                      fontSize: 14,
                      color: isGood ? 'success.main' : 'error.main',
                    }}
                  />
                )}
                <Typography
                  variant='body2'
                  sx={{
                    color: isGood
                      ? 'success.main'
                      : isBad
                        ? 'error.main'
                        : 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  {Math.abs(change)}%
                </Typography>
              </Stack>
            )}
            {previousLabel && (
              <Typography variant='caption' color='text.secondary'>
                {previousLabel}
              </Typography>
            )}
            {subtext && (
              <Typography variant='caption' color='text.secondary'>
                {subtext}
              </Typography>
            )}
          </Stack>
          {icon && (
            <Avatar
              sx={{
                backgroundColor: 'primary.main',
                height: 48,
                width: 48,
              }}
            >
              {icon}
            </Avatar>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export const formatMoney = (value: number) =>
  `QAR ${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

export const SectionHeader = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      justifyContent: 'space-between',
      alignItems: { xs: 'stretch', md: 'center' },
      gap: { xs: 1.5, md: 2 },
      mb: 2,
      minWidth: 0,
    }}
  >
    <Box sx={{ minWidth: 0, flexShrink: 0 }}>
      <Typography variant='h6'>{title}</Typography>
      {subtitle && (
        <Typography variant='body2' color='text.secondary'>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action ? (
      <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
        {action}
      </Box>
    ) : null}
  </Box>
);
